import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MonitoredIdentityDto, IdType } from '../services';
import { formatNationalId } from '../utils/nationalIdFormatter';

interface DisplayNameProps {
  identity: MonitoredIdentityDto;
  variant?: 'default' | 'compact';
  showIdType?: boolean;
}

const DisplayName: React.FC<DisplayNameProps> = ({ 
  identity, 
  variant = 'default',
  showIdType = true 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine if we have a resolved name
  const hasResolvedName = Boolean(identity.nationalId?.fullName);
  
  // Get the display name - prioritize resolved name from nationalId
  let displayName = hasResolvedName 
    ? identity.nationalId.fullName
    : identity.fullName; // Fallback to legacy fullName for existing data
    
  // If no full name available, construct from parts
  if (!displayName && identity.nationalId) {
    const parts = [
      identity.nationalId.firstName,
      identity.nationalId.middleNames,
      identity.nationalId.lastName1,
      identity.nationalId.lastName2
    ].filter(Boolean);
    
    if (parts.length > 0) {
      displayName = parts.join(' ');
    }
  }
  
  // Format national ID for fallback display
  const formattedId = identity.nationalId ? formatNationalId(
    identity.nationalId.normalizedId || '', 
    identity.nationalId.idType || IdType.PersonalId
  ) : null;
  
  // Final fallback to nationalIdId if everything else is empty
  const fallbackDisplay = formattedId || identity.nationalIdId;
  
  // Determine what to show
  const showName = Boolean(displayName);
  
  // Responsive design adjustments
  const chipSize = isMobile ? 'small' : 'small';
  const iconSize = isMobile ? 16 : 18;
  const spacing = isMobile ? 1 : 1.5;
  
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showName ? (
          <>
            <Typography variant="body2" noWrap>
              {displayName}
            </Typography>
            {hasResolvedName && (
              <Tooltip title={t('identities.nameResolved')}>
                <CheckCircleIcon 
                  color="success" 
                  sx={{ fontSize: 14 }} 
                />
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              {fallbackDisplay}
            </Typography>
            <Tooltip title={t('identities.nameResolutionPending')}>
              <WarningIcon 
                color="warning" 
                sx={{ fontSize: 14 }} 
              />
            </Tooltip>
          </>
        )}
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing, flexWrap: 'wrap' }}>
      {showName ? (
        <>
          <Typography 
            variant="h6" 
            component="span"
            sx={{ 
              wordBreak: 'break-word',
              ...(isMobile && { fontSize: '1rem' })
            }}
          >
            {displayName}
          </Typography>
          {hasResolvedName && (
            <Tooltip title={t('identities.nameResolved')}>
              <Chip
                icon={<CheckCircleIcon />}
                label={t('identities.official')}
                size={chipSize}
                color="success"
                variant="outlined"
                sx={{ 
                  '& .MuiChip-icon': { 
                    fontSize: iconSize 
                  }
                }}
              />
            </Tooltip>
          )}
        </>
      ) : (
        <>
          <Typography 
            variant="h6" 
            component="span" 
            color="text.secondary"
            sx={{ 
              wordBreak: 'break-word',
              ...(isMobile && { fontSize: '1rem' })
            }}
          >
            {fallbackDisplay}
          </Typography>
          <Tooltip 
            title={t('identities.nameResolutionPending')}
            placement={isMobile ? 'top' : 'right'}
          >
            <Chip
              icon={<WarningIcon />}
              label={t('identities.pendingResolution')}
              size={chipSize}
              color="warning"
              variant="outlined"
              sx={{ 
                '& .MuiChip-icon': { 
                  fontSize: iconSize 
                }
              }}
            />
          </Tooltip>
        </>
      )}
      
      {showIdType && identity.nationalId?.idType !== undefined && (
        <Chip
          label={
            identity.nationalId.idType === IdType.PersonalId 
              ? t('registration.personalId') 
              : t('registration.companyId')
          }
          size={chipSize}
          color={identity.nationalId.idType === IdType.PersonalId ? 'primary' : 'secondary'}
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default DisplayName;